import React, { useState, useEffect } from "react";
import { Bell, X, Check, AlertTriangle, Info, XCircle, CheckCircle } from "lucide-react";
import { useHeader } from "@/shared/contexts/use-header";
import type { Notification } from "@/shared/contexts/HeaderContext.types";
import { cn } from "@/shared/utils";

interface NotificationCenterProps {
  className?: string;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ className }) => {
  const {
    notifications,
    unreadCount,
    isNotificationPanelOpen,
    toggleNotificationPanel,
    markAsRead,
    markAllAsRead,
    removeNotification,
  } = useHeader();

  return (
    <div className={className}>
      {/* Notification Bell */}
      <button
        onClick={toggleNotificationPanel}
        className="relative p-2 rounded-lg hover:bg-neutral-800/80 transition-all duration-200"
      >
        <Bell className="w-5 h-5 text-neutral-400 hover:text-white transition-colors" />

        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-neutral-900 flex items-center justify-center">
            <span className="text-xs text-white font-bold">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isNotificationPanelOpen && (
        <NotificationPanel
          notifications={notifications}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onRemove={removeNotification}
          onClose={toggleNotificationPanel}
        />
      )}
    </div>
  );
};

interface NotificationPanelProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onRemove: (id: string) => void;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onRemove,
  onClose,
}) => {
  const unreadNotifications = notifications.filter((n) => !n.read);
  const unreadCount = unreadNotifications.length;

  return (
    <div className="fixed inset-0 z-40">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="absolute top-16 right-4 w-96 bg-neutral-800 rounded-xl border border-neutral-700/50 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-700/50">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-neutral-400" />
            <h3 className="font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                {unreadCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllAsRead}
                className="text-xs text-neutral-400 hover:text-white transition-colors"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 text-neutral-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-neutral-400">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="py-2">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={onMarkAsRead}
                  onRemove={onRemove}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onRemove: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onRemove,
}) => {
  const getIcon = () => {
    switch (notification.type) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case "error":
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Info className="w-4 h-4 text-blue-400" />;
    }
  };

  const getTypeColor = () => {
    switch (notification.type) {
      case "success":
        return "border-l-green-500";
      case "warning":
        return "border-l-yellow-500";
      case "error":
        return "border-l-red-500";
      default:
        return "border-l-blue-500";
    }
  };

  return (
    <div
      className={cn(
        "relative border-l-4 p-4 hover:bg-neutral-800/50 transition-colors",
        getTypeColor(),
        !notification.read && "bg-neutral-800/30",
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          {getIcon()}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-white text-sm">{notification.title}</h4>
            <p className="text-neutral-400 text-sm mt-1">{notification.message}</p>
            <p className="text-neutral-500 text-xs mt-2">
              {notification.timestamp.toLocaleTimeString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {!notification.read && (
            <button
              onClick={() => onMarkAsRead(notification.id)}
              className="p-1 text-neutral-400 hover:text-white transition-colors"
              title="Mark as read"
            >
              <Check className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => onRemove(notification.id)}
            className="p-1 text-neutral-400 hover:text-red-400 transition-colors"
            title="Remove notification"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Action */}
      {notification.action && (
        <button
          onClick={notification.action.onClick}
          className="mt-3 text-xs text-blue-400 hover:text-blue-300 transition-colors"
        >
          {notification.action.label}
        </button>
      )}
    </div>
  );
};

// Toast Notifications
export const ToastContainer: React.FC = () => {
  const { notifications } = useHeader();
  const recentNotifications = notifications.slice(0, 3); // Show only 3 most recent

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {recentNotifications.map((notification: Notification) => (
        <ToastNotification key={notification.id} notification={notification} />
      ))}
    </div>
  );
};

interface ToastNotificationProps {
  notification: Notification;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ notification }) => {
  const [isVisible, setIsVisible] = useState(true);
  const { removeNotification } = useHeader();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => removeNotification(notification.id), 300);
    }, 5000);

    return () => clearTimeout(timer);
  }, [notification.id, removeNotification]);

  const getIcon = () => {
    switch (notification.type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case "error":
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  const getTypeStyles = () => {
    switch (notification.type) {
      case "success":
        return "bg-green-900/90 border-green-500/50";
      case "warning":
        return "bg-yellow-900/90 border-yellow-500/50";
      case "error":
        return "bg-red-900/90 border-red-500/50";
      default:
        return "bg-blue-900/90 border-blue-500/50";
    }
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg border backdrop-blur-sm shadow-lg transition-all duration-300",
        getTypeStyles(),
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0",
      )}
    >
      {getIcon()}
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-white text-sm">{notification.title}</h4>
        <p className="text-neutral-300 text-sm mt-1">{notification.message}</p>
      </div>
      <button
        onClick={() => {
          setIsVisible(false);
          setTimeout(() => removeNotification(notification.id), 300);
        }}
        className="p-1 text-neutral-400 hover:text-white transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
